"use client";

import { c as _c } from "react/compiler-runtime";
import { classifyProviderError, type MagnusUIMessage, PROVIDER_ERROR_COPY, type ProviderErrorCode } from "@bg-bgi/magnus-sdk";
import { Button } from "@workspace/ui/components/button";
import { AlertTriangle, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
const FALLBACK_TITLE = "Couldn't complete this response";
const FALLBACK_BODY = "This is usually a temporary issue with the AI provider. Try again, or pick a different model.";

/**
 * Resolve tiered `{ title, body }` copy for a transport-level error (one that
 * fired before the workflow could persist any metadata). `undefined` when the
 * error chain carries no recognizable gateway/HTTP signal.
 */
export function classifyTransportError(error: unknown): {
  code: ProviderErrorCode;
  title: string;
  body: string;
} | undefined {
  const code = classifyProviderError(error);
  if (!code) {
    return undefined;
  }
  const copy = PROVIDER_ERROR_COPY[code];
  return {
    code,
    title: copy.title,
    body: copy.body
  };
}

/**
 * Visibility for the transport banner: hidden once dismissed (re-armed by the
 * next error) and whenever the last assistant turn carries a persisted
 * `status: "error"` — its own message-level indicator already reports the
 * failure, so showing both would say it twice.
 */
export function useTransportErrorBanner(t0) {
  const $ = _c(8);
  const {
    error,
    messages
  } = t0;
  const [dismissed, setDismissed] = useState(false);
  let t1;
  let t2;
  if ($[0] !== error) {
    t1 = () => {
      if (error) {
        setDismissed(false);
      }
    };
    t2 = [error];
    $[0] = error;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== messages) {
    t3 = messages.findLast(_temp);
    $[3] = messages;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const lastAssistant = t3;
  const lastAssistantHasPersistedError = lastAssistant?.metadata?.status === "error";
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setDismissed(true);
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const dismiss = t4;
  const t5 = Boolean(error) && !dismissed && !lastAssistantHasPersistedError;
  let t6;
  if ($[6] !== t5) {
    t6 = {
      dismiss,
      visible: t5
    };
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
}

/**
 * Transport-level error banner, mounted at the bottom of the transcript.
 * Logs the raw error (classification strips structure, so unclassified
 * failures need the original shape) and shows the provider-error copy, or a
 * generic retry hint rather than the SDK's often-useless `error.message`.
 */
function _temp(message) {
  return message.role === "assistant";
}
export function TransportErrorBanner(t0) {
  const $ = _c(18);
  const {
    error,
    onDismissError
  } = t0;
  let t1;
  let t2;
  if ($[0] !== error) {
    t1 = () => {
      console.error("[magnus-chat] transport error", {
        name: error.name,
        message: error.message,
        cause: (error as {
          cause?: unknown;
        }).cause,
        stack: error.stack
      });
    };
    t2 = [error];
    $[0] = error;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== error) {
    t3 = classifyTransportError(error);
    $[3] = error;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const transport = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <AlertTriangle className="mt-0.5 size-5 shrink-0" />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = transport?.title ?? FALLBACK_TITLE;
  let t6;
  if ($[6] !== t5) {
    t6 = <p className="font-medium text-sm">{t5}</p>;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = transport?.body ?? FALLBACK_BODY;
  let t8;
  if ($[8] !== t7) {
    t8 = <p className="mt-1 text-sm text-warning/80">{t7}</p>;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t6 || $[11] !== t8) {
    t9 = <div className="min-w-0 flex-1">{t6}{t8}</div>;
    $[10] = t6;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== onDismissError) {
    t10 = onDismissError && <Button className="size-6 text-warning/60 hover:bg-warning/10 hover:text-warning" onClick={onDismissError} size="icon" variant="ghost"><X className="size-4" /><span className="sr-only">Dismiss</span></Button>;
    $[13] = onDismissError;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t10 || $[16] !== t9) {
    t11 = <div className="flex items-start gap-3 rounded-lg border border-warning/20 bg-warning/10 p-4 text-warning" role="status">{t4}{t9}{t10}</div>;
    $[15] = t10;
    $[16] = t9;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  return t11;
}